import React, { lazy } from 'react';
import { Navigate } from 'react-router-dom';
import type { RouteObject } from 'react-router-dom';

/* Route table.
 *
 * The inspection modules are CHILDREN of /inspect, not siblings in the menu.
 * They were top-level screens that each guessed which inspection they were
 * about, so a user who started an inspection at one branch and opened "Gold"
 * saw another branch's packets. They are steps of one open inspection now and
 * the old paths redirect into it. */

const DashboardPage = lazy(() => import('../pages/dashboard/DashboardPage'));
const InspectionShell = lazy(() => import('../pages/inspect/InspectionShell'));
const BranchInspectionPage = lazy(() => import('../pages/inspect/BranchInspectionPage'));
const LoanEnginePage = lazy(() => import('../pages/inspect/LoanEnginePage'));
const GoldPage = lazy(() => import('../pages/inspect/GoldPage'));
const CashPage = lazy(() => import('../pages/inspect/CashPage'));
const KycPage = lazy(() => import('../pages/inspect/KycPage'));
const ItCyberPage = lazy(() => import('../pages/inspect/ItCyberPage'));
const InspectionPlanningPage = lazy(() => import('../pages/plan/InspectionPlanningPage'));
const MastersPage = lazy(() => import('../pages/plan/MastersPage'));
const PreInspectionPage = lazy(() => import('../pages/plan/PreInspectionPage'));
const ObservationsPage = lazy(() => import('../pages/followup/ObservationsPage'));
const RiskScoringPage = lazy(() => import('../pages/followup/RiskScoringPage'));
const ShgArrearsPage = lazy(() => import('../pages/followup/ShgArrearsPage'));
const ReportsPage = lazy(() => import('../pages/system/ReportsPage'));
const BankExplorerPage = lazy(() => import('../pages/overview/BankExplorerPage'));
const BranchInspectionSetupPage = lazy(() => import('../pages/inspect/BranchInspectionSetupPage'));
const BranchBookStep = lazy(() => import('../pages/inspect/BranchBookStep'));
const AdminPage = lazy(() => import('../pages/system/AdminPage'));
const ChangePasswordPage = lazy(() => import('../pages/account/ChangePasswordPage'));

/* Lazy chunks can't be named inline in the children list, so the two steps that
   live in one file are pulled out here. */
const ObservationsStep = lazy(() =>
  import('../pages/inspect/SignoffStep').then((m) => ({ default: m.ObservationsStep })),
);
const SignoffStep = lazy(() =>
  import('../pages/inspect/SignoffStep').then((m) => ({ default: m.SignoffStep })),
);

export const contentRoutes: RouteObject[] = [
  { path: '/dashboard', element: <DashboardPage /> },
  { path: '/planning', element: <InspectionPlanningPage /> },
  { path: '/masters', element: <MastersPage /> },
  { path: '/pre', element: <PreInspectionPage /> },

  /* Choosing the branch comes before the workspace, so it sits outside it. */
  { path: '/inspect/start', element: <BranchInspectionSetupPage /> },

  {
    path: '/inspect',
    element: <InspectionShell />,
    children: [
      { index: true, element: <Navigate to="/inspect/checklist" replace /> },
      { path: 'checklist', element: <BranchInspectionPage /> },
      { path: 'book', element: <BranchBookStep /> },
      { path: 'loans', element: <LoanEnginePage /> },
      { path: 'gold', element: <GoldPage /> },
      { path: 'cash', element: <CashPage /> },
      { path: 'kyc', element: <KycPage /> },
      { path: 'it', element: <ItCyberPage /> },
      { path: 'observations', element: <ObservationsStep /> },
      { path: 'signoff', element: <SignoffStep /> },
    ],
  },

  /* Old top-level module paths — anyone with a bookmark lands on the step. */
  { path: '/loans', element: <Navigate to="/inspect/loans" replace /> },
  { path: '/gold', element: <Navigate to="/inspect/gold" replace /> },
  { path: '/cash', element: <Navigate to="/inspect/cash" replace /> },
  { path: '/kyc', element: <Navigate to="/inspect/kyc" replace /> },
  { path: '/it', element: <Navigate to="/inspect/it" replace /> },

  { path: '/obs', element: <ObservationsPage /> },
  { path: '/scoring', element: <RiskScoringPage /> },
  { path: '/shg', element: <ShgArrearsPage /> },
  { path: '/reports', element: <ReportsPage /> },
  { path: '/explorer', element: <BankExplorerPage /> },
  { path: '/admin', element: <AdminPage /> },
  { path: '/account/password', element: <ChangePasswordPage /> },
];

export default contentRoutes;
